import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiClient from '../../services/api';
import * as notificationService from '../../services/notificationService';
import type { Notification } from '../../services/notificationService';

interface StaffMember {
  id: string;
  name: string;
  email: string;
  isActive: boolean;
  isAgency: boolean;
  role: { name: string } | string;
  userSkills: Array<{ skill: { name: string } }>;
}

interface DashboardStats {
  totalUsers: number;
  activeUsers: number;
  byRole: Record<string, number>;
  skillCounts: Record<string, number>;
  agencyCount: number;
  inactiveCount: number;
}

const roleBadgeColor: Record<string, string> = {
  'Controller': 'bg-purple-100 text-purple-800',
  'Clearer': 'bg-blue-100 text-blue-800',
  'Office': 'bg-gray-100 text-gray-700',
  'Warehouse': 'bg-yellow-100 text-yellow-800',
  'Super Admin': 'bg-rose-100 text-rose-800',
};

const StatCard = ({ label, value, sub, color }: { label: string; value: number | string; sub?: string; color: string }) => (
  <div className={`rounded-xl border p-5 flex flex-col gap-1 ${color}`}>
    <span className="text-xs font-semibold uppercase tracking-wider opacity-70">{label}</span>
    <span className="text-3xl font-bold">{value}</span>
    {sub && <span className="text-xs opacity-60">{sub}</span>}
  </div>
);

const AdminDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await apiClient.get('/users');
        const users: StaffMember[] = response.data.data ?? response.data ?? [];
        setStaff(users);

        const byRole: Record<string, number> = {};
        const skillCounts: Record<string, number> = {};
        let activeUsers = 0;
        let agencyCount = 0;

        users.forEach(u => {
          const roleName = typeof u.role === 'string' ? u.role : u.role?.name ?? 'Unknown';
          byRole[roleName] = (byRole[roleName] ?? 0) + 1;
          if (u.isActive) activeUsers++;
          if (u.isAgency) agencyCount++;
          u.userSkills?.forEach(us => {
            const sn = us.skill.name;
            skillCounts[sn] = (skillCounts[sn] ?? 0) + 1;
          });
        });

        setStats({
          totalUsers: users.length,
          activeUsers,
          byRole,
          skillCounts,
          agencyCount,
          inactiveCount: users.length - activeUsers,
        });
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchNotifications = async () => {
      try {
        const data = await notificationService.getNotifications(10);
        setNotifications(data?.notifications ?? []);
        setUnreadCount(data?.unreadCount ?? 0);
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      }
    };

    fetchStats();
    fetchNotifications();
  }, []);

  const handleMarkAllRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleMarkRead = async (id: string) => {
    try {
      await notificationService.markAsRead(id);
      setNotifications(prev => prev.map(n => (n.id === id ? { ...n, isRead: true } : n)));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-3 text-slate-500">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
        Loading dashboard...
      </div>
    );
  }

  const userName = user?.name ?? 'Admin';
  const today = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="p-8 max-w-7xl space-y-8">

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Dashboard</h1>
          <p className="text-sm text-slate-400 mt-0.5">{today} · Logged in as {userName}</p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/users/create"
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors">
            + New User
          </Link>
          <Link to="/admin/roster"
            className="px-4 py-2 bg-slate-800 text-white text-sm font-medium rounded-lg hover:bg-slate-900 transition-colors">
            Open Roster
          </Link>
        </div>
      </div>

      {/* Top stat cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total Staff" value={stats.totalUsers} sub={`${stats.activeUsers} active · ${stats.inactiveCount} inactive`} color="bg-white border-slate-200 text-slate-800" />
          <StatCard label="Agency Staff" value={stats.agencyCount} sub="External contractors" color="bg-amber-50 border-amber-200 text-amber-900" />
          <StatCard label="Roles in System" value={Object.keys(stats.byRole).length} sub={Object.entries(stats.byRole).map(([r, c]) => `${c} ${r}`).join(' · ')} color="bg-blue-50 border-blue-200 text-blue-900" />
          <StatCard label="Skill Types" value={Object.keys(stats.skillCounts).length} sub="Across all staff" color="bg-emerald-50 border-emerald-200 text-emerald-900" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Staff by Role breakdown */}
        {stats && (
          <div className="lg:col-span-1 bg-white border border-slate-200 rounded-xl p-5">
            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-4">Staff by Role</h2>
            <div className="space-y-3">
              {Object.entries(stats.byRole).sort((a, b) => b[1] - a[1]).map(([role, count]) => (
                <div key={role} className="flex items-center justify-between">
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${roleBadgeColor[role] ?? 'bg-slate-100 text-slate-700'}`}>
                    {role}
                  </span>
                  <div className="flex items-center gap-2 flex-1 mx-3">
                    <div className="flex-1 bg-slate-100 rounded-full h-1.5">
                      <div className="bg-blue-500 h-1.5 rounded-full" style={{ width: `${(count / (stats.totalUsers || 1)) * 100}%` }} />
                    </div>
                  </div>
                  <span className="text-sm font-bold text-slate-700 w-6 text-right">{count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Skill breakdown */}
        {stats && (
          <div className="lg:col-span-1 bg-white border border-slate-200 rounded-xl p-5">
            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-4">Skills Coverage</h2>
            <div className="space-y-3">
              {Object.entries(stats.skillCounts).sort((a, b) => b[1] - a[1]).map(([skill, count]) => (
                <div key={skill} className="flex items-center justify-between">
                  <span className="text-sm text-slate-600 truncate flex-1">{skill}</span>
                  <div className="flex items-center gap-2 mx-3 w-24">
                    <div className="flex-1 bg-slate-100 rounded-full h-1.5">
                      <div className="bg-emerald-500 h-1.5 rounded-full" style={{ width: `${(count / (stats.totalUsers || 1)) * 100}%` }} />
                    </div>
                  </div>
                  <span className="text-sm font-bold text-slate-700 w-6 text-right">{count}</span>
                </div>
              ))}
              {Object.keys(stats.skillCounts).length === 0 && (
                <p className="text-sm text-slate-400">No skills assigned yet</p>
              )}
            </div>
          </div>
        )}

        {/* Notifications */}
        <div className="lg:col-span-1 bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-2">
              Notifications
              {unreadCount > 0 && (
                <span className="px-1.5 py-0.5 text-xs font-bold bg-red-500 text-white rounded-full">{unreadCount}</span>
              )}
            </h2>
            {unreadCount > 0 && (
              <button onClick={handleMarkAllRead} className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                Mark all read
              </button>
            )}
          </div>
          {notifications.length === 0 ? (
            <p className="text-sm text-slate-400">No notifications</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {notifications.map(n => (
                <div key={n.id}
                  onClick={() => !n.isRead && handleMarkRead(n.id)}
                  className={`p-3 rounded-lg border text-sm cursor-pointer transition-all ${n.isRead ? 'bg-white border-slate-100 text-slate-600' : 'bg-blue-50 border-blue-200 text-slate-800'}`}>
                  <div className="flex items-center gap-2 mb-0.5">
                    {!n.isRead && <span className="w-1.5 h-1.5 bg-blue-500 rounded-full flex-shrink-0" />}
                    <span className="font-semibold text-xs">{n.title}</span>
                  </div>
                  <p className="text-xs text-slate-500">{n.message}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Staff table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">All Staff</h2>
          <Link to="/admin/users" className="text-xs text-blue-600 hover:text-blue-800 font-medium">Manage users →</Link>
        </div>
        <div className="overflow-auto max-h-72">
          <table className="w-full text-xs">
            <thead className="sticky top-0 z-10 bg-slate-50">
              <tr className="border-b border-slate-100">
                <th className="text-left px-4 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Name</th>
                <th className="text-left px-4 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Role</th>
                <th className="text-left px-4 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Skills</th>
                <th className="text-left px-4 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Type</th>
                <th className="text-left px-4 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {staff.map(s => {
                const roleName = typeof s.role === 'string' ? s.role : s.role?.name ?? '—';
                const skills = s.userSkills?.map(us => us.skill.name) ?? [];
                return (
                  <tr key={s.id} className="hover:bg-slate-50/60 transition-colors">
                    <td className="px-4 py-2">
                      <div className="font-medium text-slate-800">{s.name}</div>
                      <div className="text-[10px] text-slate-400">{s.email}</div>
                    </td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${roleBadgeColor[roleName] ?? 'bg-slate-100 text-slate-700'}`}>
                        {roleName}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap gap-1">
                        {skills.length > 0
                          ? skills.map(sk => (
                            <span key={sk} className="px-1.5 py-0.5 bg-slate-100 text-slate-600 text-[10px] rounded">{sk}</span>
                          ))
                          : <span className="text-slate-300 text-[10px]">—</span>
                        }
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      {s.isAgency
                        ? <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] rounded-full font-medium">Agency</span>
                        : <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-[10px] rounded-full">Permanent</span>
                      }
                    </td>
                    <td className="px-4 py-2">
                      {s.isActive
                        ? <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700"><span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />Active</span>
                        : <span className="inline-flex items-center gap-1 text-[10px] font-medium text-slate-400"><span className="w-1.5 h-1.5 bg-slate-300 rounded-full" />Inactive</span>
                      }
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3">
        <Link to="/admin/users" className="px-4 py-2 border border-slate-200 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors">View All Users</Link>
        <Link to="/admin/users/create" className="px-4 py-2 border border-slate-200 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors">Create User</Link>
        <Link to="/admin/roster" className="px-4 py-2 border border-slate-200 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors">Roster</Link>
        <Link to="/admin/approvals" className="px-4 py-2 border border-slate-200 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors">Approvals</Link>
        <Link to="/admin/skills" className="px-4 py-2 border border-slate-200 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors">Manage Skills</Link>
      </div>

    </div>
  );
};

export default AdminDashboard;
